import { useEffect, useRef, useState } from 'react'
import { AvisoSaldoDivergente } from '../../components/AvisoSaldoDivergente'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PluggyConnect } from 'react-pluggy-connect'
import {
  pluggyContasApi, pluggyVinculosApi, pluggyPendenciasApi, pluggyHistoricoApi, regrasCategorizacaoApi,
  contasApi, cartoesApi, categoriasApi,
} from '../../api/endpoints'
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/EmptyState'
import { Button, IconButton } from '../../components/ui/Button'
import { Card, CardHeader, AvatarIcon } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Pagination, Tabs } from '../../components/ui/Navigation'
import { Table, THead, Th, ThOrdenavel, Tr, Td } from '../../components/ui/Table'
import { PageHeader } from '../../layout/PageHeader'
import { useToast } from '../../components/ui/Toast'
import {
  AccountType, ACCOUNT_TYPE_LABEL, PluggyPendenciaTipo, PluggyConfiguracaoStatus,
  CampoRegraCategorizacao, CAMPO_REGRA_LABEL, OperadorRegraCategorizacao, OPERADOR_REGRA_LABEL, OPERADORES_POR_CAMPO,
} from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import { mensagemErroPluggy } from '../../lib/pluggyErro'
import { AssistenteCorte } from './AssistenteCorte'
import { AvisoConexao } from './AvisoConexao'
import { RefazerConfiguracao } from './RefazerConfiguracao'
import { ExcluirDuplicada, DialogoDuplicidade } from './ExcluirDuplicada'
import { usePluggyStatus, revalidarSePluggyDesligada } from './usePluggyStatus'
import { AvisoPluggyDesligada } from './AvisoPluggyDesligada'
import { invalidarPluggy, itemAbreviado, type OpcaoConexao } from './invalidar'
import { AceitarTudoButton } from './AceitarTudo'
import { PagamentoContaSituacao } from './PagamentoContaPendencia'
import { useOrdenarBuscarLocal } from '../../lib/ordenarLancamentos'
import type {
  ContaPluggyInfo, TransacaoPluggyInfo, FinancialAccount, CreditCard, PluggyVinculoInfo, PluggyPendenciaInfo,
  Categoria, CondicaoRegra, RegraCategorizacao, DuplicidadeInfo,
} from '../../types'

const PLUGGY_TIPO_LABEL: Record<string, string> = {
  BANK: 'Conta',
  CREDIT: 'Crédito',
}

const CRIAR_NOVA = '__criar_nova__'

const ABAS = [
  { key: 'contas' as const, label: 'Contas', icon: 'ri-bank-line' },
  { key: 'historico' as const, label: 'Histórico', icon: 'ri-history-line' },
  { key: 'categorias' as const, label: 'Regras de categoria', icon: 'ri-price-tag-3-line' },
]

export function PluggyContasPage() {
  const [aba, setAba] = useState<'contas' | 'historico' | 'categorias'>('contas')
  const [conta, setConta] = useState<ContaPluggyInfo | null>(null)
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [cursores, setCursores] = useState<(string | undefined)[]>([undefined])
  const [conectar, setConectar] = useState<{ accessToken: string; incluirSandbox: boolean } | null>(null)
  const [vinculando, setVinculando] = useState<ContaPluggyInfo | null>(null)
  const [configurandoId, setConfigurandoId] = useState<string | null>(null)
  const [refazendo, setRefazendo] = useState<{ id: string; nome?: string } | null>(null)
  const itemRegistrado = useRef<string | null>(null)
  // Atualizar conexão: itemId da conexão existente (aviso -> token com itemId -> widget em modo de atualização).
  const [atualizarItemId, setAtualizarItemId] = useState<string | null>(null)
  const [itemNoWidget, setItemNoWidget] = useState<string | null>(null)
  const [duplicidade, setDuplicidade] = useState<{ itemNovoId: string; dados: DuplicidadeInfo } | null>(null)
  const [excluindo, setExcluindo] = useState<{
    itemId: string; banco: string; rotuloItem: string; opcoes: OpcaoConexao[]; sugerido?: string; mantidoFixo?: string
  } | null>(null)
  // ?conectar=1 (vindo de Primeiros passos / Contas) abre direto o aviso que antecede a conexão.
  const [params, setParams] = useSearchParams()
  const [aviso, setAviso] = useState(() => params.get('conectar') === '1')
  useEffect(() => {
    if (params.get('conectar') === '1') setParams({}, { replace: true })
  }, [params, setParams])
  const qc = useQueryClient()
  const toast = useToast()
  const status = usePluggyStatus()
  const desligada = !status.ativa

  const registrarItem = useMutation({
    mutationFn: pluggyContasApi.registrarItem,
    onSuccess: (r) => {
      toast.success('Banco conectado.')
      invalidarPluggy(qc)
      // API antiga devolve data nulo; sem duplicidade nada muda.
      if (r?.duplicidade && r.duplicidade.contas?.length) setDuplicidade({ itemNovoId: r.itemId, dados: r.duplicidade })
    },
    onError: (e: Error) => { revalidarSePluggyDesligada(qc, e); toast.error(e.message || 'A conexão foi criada, mas não foi possível registrá-la.') },
  })

  // O widget às vezes mostra a mensagem de sucesso dele mas trava numa etapa própria antes de chamar onSuccess
  // (ex.: consentimento de Open Finance). O ITEM_RESPONSE já traz o item logo após ele ser criado, então
  // registramos por ele e usamos o onSuccess só como reforço, sem duplicar (itemRegistrado).
  function registrarSeNovo(itemId: string) {
    if (itemRegistrado.current === itemId) return
    itemRegistrado.current = itemId
    registrarItem.mutate(itemId)
  }

  const pedirToken = useMutation({
    mutationFn: (itemId?: string) => pluggyContasApi.connectToken(itemId),
    onSuccess: (t) => setConectar(t),
    onError: (e: Error) => { revalidarSePluggyDesligada(qc, e); toast.error(mensagemErroPluggy(e, 'Não foi possível abrir a conexão.')) },
  })

  const { data: contas = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['pluggy-contas'],
    queryFn: pluggyContasApi.list,
    // Desligada, a rota responde 503: nem chama. Também espera o status, para não pedir antes de saber.
    enabled: status.pronto && !desligada,
  })

  const { data: vinculos = [] } = useQuery({ queryKey: ['pluggy-vinculos'], queryFn: pluggyVinculosApi.list })
  const { data: minhasContas = [] } = useQuery({ queryKey: ['contas'], queryFn: contasApi.list })
  const { data: meusCartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list })

  const [transacoes, setTransacoes] = useState<TransacaoPluggyInfo[]>([])
  const [proximoCursor, setProximoCursor] = useState<string | null>(null)
  const cursorAtual = cursores[cursores.length - 1]

  const { data: resultado, isLoading: loadingTx, isFetching: carregandoMais } = useQuery({
    queryKey: ['pluggy-transacoes', conta?.accountId, cursorAtual, de, ate],
    queryFn: () => pluggyContasApi.transacoes(conta!.accountId, { de: de || undefined, ate: ate || undefined, cursor: cursorAtual }),
    enabled: !!conta,
  })

  const ordenacaoExtrato = useOrdenarBuscarLocal(transacoes, {
    nome: (t) => t.descricao, data: (t) => t.data, valor: (t) => t.valor,
  })

  useEffect(() => {
    if (!resultado) return
    setTransacoes((atual) => (cursorAtual ? [...atual, ...resultado.itens] : resultado.itens))
    setProximoCursor(resultado.proximoCursor)
  }, [resultado, cursorAtual])

  // Conexões (itemId) do mesmo banco, na ordem em que aparecem; o rótulo usa a ordem e o itemId abreviado.
  const chaveBanco = (nome: string) => nome.trim().toLowerCase()
  function conexoesDoBanco(banco: string): OpcaoConexao[] {
    const ids: string[] = []
    for (const c of contas) if (chaveBanco(c.instituicaoNome) === chaveBanco(banco) && !ids.includes(c.itemId)) ids.push(c.itemId)
    return ids.map((id, i) => ({ itemId: id, rotulo: `Conexão ${i + 1} (${itemAbreviado(id)})` }))
  }
  function vinculosDoItem(itemId: string) {
    const ids = new Set(contas.filter((c) => c.itemId === itemId).map((c) => c.accountId))
    return vinculos.filter((v) => ids.has(v.pluggyAccountId)).length
  }
  function iniciarExclusao(itemId: string, banco: string, mantidoFixo?: string) {
    const todas = conexoesDoBanco(banco)
    const opcoes = todas.filter((o) => o.itemId !== itemId)
    if (mantidoFixo && !opcoes.some((o) => o.itemId === mantidoFixo)) opcoes.push({ itemId: mantidoFixo, rotulo: `Conexão nova (${itemAbreviado(mantidoFixo)})` })
    // Sugere manter a conexão que já tem os vínculos.
    const sugerido = [...opcoes].sort((a, b) => vinculosDoItem(b.itemId) - vinculosDoItem(a.itemId))[0]?.itemId
    setExcluindo({
      itemId, banco, sugerido, mantidoFixo,
      rotuloItem: todas.find((o) => o.itemId === itemId)?.rotulo ?? `Conexão (${itemAbreviado(itemId)})`,
      opcoes,
    })
  }
  function resolverDuplicidade(acao: 'usar-nova' | 'manter-antiga') {
    if (!duplicidade) return
    const { itemNovoId, dados } = duplicidade
    setDuplicidade(null)
    if (acao === 'usar-nova') iniciarExclusao(dados.itemExistenteId, dados.banco, itemNovoId)
    else iniciarExclusao(itemNovoId, dados.banco, dados.itemExistenteId)
  }

  function abrirConta(c: ContaPluggyInfo) {
    setConta(c)
    setCursores([undefined])
    setTransacoes([])
    setProximoCursor(null)
  }

  const itensConectados: OpcaoConexao[] = Array.from(new Map(contas.map((c) => [c.itemId, c])).values()).map((c) => ({
    itemId: c.itemId,
    rotulo: `${c.instituicaoNome} (${itemAbreviado(c.itemId)})`,
  }))
  const gruposMapa = new Map<string, ContaPluggyInfo[]>()
  for (const c of contas) {
    if (c.ehDuplicada && c.grupoDuplicada) gruposMapa.set(c.grupoDuplicada, [...(gruposMapa.get(c.grupoDuplicada) ?? []), c])
  }
  const grupos = Array.from(gruposMapa.entries())
  const semGrupo = contas.filter((c) => !(c.ehDuplicada && c.grupoDuplicada))

  function cartaoConta(c: ContaPluggyInfo, dup: boolean) {
    const vinculo = vinculos.find((v) => v.pluggyAccountId === c.accountId)
    const alvo = vinculo?.financialAccountId
      ? minhasContas.find((a) => a.id === vinculo.financialAccountId)?.name
      : vinculo?.cartaoId
        ? meusCartoes.find((k) => k.id === vinculo.cartaoId)?.nome
        : undefined
    const nomeVinculo = c.vinculadaA ?? alvo
    const nConexao = conexoesDoBanco(c.instituicaoNome).findIndex((o) => o.itemId === c.itemId) + 1
    const nVinculos = c.vinculosNaMesmaConta ?? vinculo?.vinculosNaMesmaConta ?? 0
    return (
      <div key={c.accountId} className="flex flex-col rounded border border-border/70 bg-surface2/40 p-4 transition-colors hover:border-primary/50">
        <div className="flex items-start justify-between gap-2">
          <AvatarIcon icon={c.tipo === 'CREDIT' ? 'ri-bank-card-line' : 'ri-bank-line'} tone={c.tipo === 'CREDIT' ? 'expense' : 'info'} />
          <div className="flex flex-wrap justify-end gap-1">
            {c.ehDuplicada && <Badge variant="warning">Possível duplicada</Badge>}
            <Badge variant={c.tipo === 'CREDIT' ? 'expense' : 'info'}>{PLUGGY_TIPO_LABEL[c.tipo] ?? c.tipo}</Badge>
          </div>
        </div>
        <button type="button" onClick={() => abrirConta(c)} className="text-left">
          <p className="mt-3 truncate text-2xs uppercase text-muted">{c.instituicaoNome}</p>
          <p className="truncate font-medium text-white">{c.nome}{c.numeroFinal ? <span className="font-normal text-muted"> · final {c.numeroFinal}</span> : null}</p>
          <p className={`mt-2 text-xl font-semibold ${c.saldo >= 0 ? 'text-white' : 'text-expense'}`}>{formatMoney(c.saldo)}</p>
          <p className="mt-2 flex items-center gap-1 text-2xs text-primary">Ver transações <i className="ri-arrow-right-line" aria-hidden /></p>
        </button>
        {dup && (
          <p className="mt-2 text-2xs text-muted">
            Conexão {nConexao > 0 ? nConexao : ''} ({itemAbreviado(c.itemId)}) · {nomeVinculo ? <>vinculada a <strong className="text-white">{nomeVinculo}</strong></> : 'sem vínculo'}
          </p>
        )}
        {nVinculos > 1 && (
          <p className="mt-2 flex items-start gap-1 rounded bg-warning/10 px-2 py-1 text-2xs text-warning" role="note">
            <i className="ri-error-warning-line" aria-hidden /> Esta conta tem {nVinculos} vínculos da Pluggy.
          </p>
        )}
        {(c.saldoDivergente ?? vinculo?.saldoDivergente) && (
          <AvisoSaldoDivergente divergencia={(c.saldoDivergente ?? vinculo?.saldoDivergente)!} bancoAtualizadoEm={c.bancoAtualizadoEm ?? vinculo?.bancoAtualizadoEm} />
        )}
        <div className="mt-3 space-y-2 border-t border-border/60 pt-2">
          {vinculo ? (
            <VinculoInfo
              vinculo={vinculo} nomeAlvo={alvo}
              onConfigurar={() => setConfigurandoId(vinculo.id)}
              onRefazer={() => setRefazendo({ id: vinculo.id, nome: alvo })}
            />
          ) : (
            <Button data-tutorial="pluggy.vincular" variant="soft-primary" icon="ri-link" onClick={() => setVinculando(c)} className="w-full justify-center">Vincular</Button>
          )}
          {dup && (
            <Button variant="light" size="sm" icon="ri-delete-bin-line" className="w-full justify-center" onClick={() => iniciarExclusao(c.itemId, c.instituicaoNome)}>
              Excluir conexão duplicada
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (conta) {
    return (
      <>
        <PageHeader title="Contas Pluggy" />
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-3">
                <button type="button" onClick={() => setConta(null)} className="flex h-8 w-8 items-center justify-center rounded bg-surface2 text-muted hover:text-white" aria-label="Voltar para contas">
                  <i className="ri-arrow-left-line" aria-hidden />
                </button>
                <span>{conta.instituicaoNome} · {conta.nome}</span>
                <span className={`text-base font-semibold ${conta.saldo >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(conta.saldo)}</span>
              </span>
            }
            actions={
              <>
                <div className="w-40"><Input type="date" value={de} onChange={(e) => { setDe(e.target.value); setCursores([undefined]); setTransacoes([]) }} aria-label="Data inicial" /></div>
                <span className="text-13 text-muted">até</span>
                <div className="w-40"><Input type="date" value={ate} onChange={(e) => { setAte(e.target.value); setCursores([undefined]); setTransacoes([]) }} aria-label="Data final" /></div>
              </>
            }
          />
          {loadingTx ? (
            <LoadingState message="Carregando transações..." />
          ) : transacoes.length === 0 ? (
            <EmptyState icon="ri-exchange-dollar-line" message="Nenhuma transação encontrada." />
          ) : (
            <>
              <div className="border-b border-border/70 p-4">
                <div className="w-64">
                  <Input placeholder="Buscar por nome" value={ordenacaoExtrato.busca} onChange={(e) => ordenacaoExtrato.setBusca(e.target.value)} />
                </div>
              </div>
              <Table>
                <THead>
                  <tr>
                    <ThOrdenavel campo="data" ativo={ordenacaoExtrato.ordenarPor} desc={ordenacaoExtrato.ordemDesc} onClick={ordenacaoExtrato.alternarOrdenacao}>Data</ThOrdenavel>
                    <ThOrdenavel campo="nome" ativo={ordenacaoExtrato.ordenarPor} desc={ordenacaoExtrato.ordemDesc} onClick={ordenacaoExtrato.alternarOrdenacao}>Descrição</ThOrdenavel>
                    <Th>Categoria</Th>
                    <ThOrdenavel campo="valor" ativo={ordenacaoExtrato.ordenarPor} desc={ordenacaoExtrato.ordemDesc} onClick={ordenacaoExtrato.alternarOrdenacao} className="text-right">Valor</ThOrdenavel>
                  </tr>
                </THead>
                <tbody>
                  {ordenacaoExtrato.itens.map((t) => (
                    <Tr key={t.transactionId}>
                      <Td className="whitespace-nowrap text-muted">{formatDate(t.data)}</Td>
                      <Td className="font-medium text-white">
                        {t.descricao}
                        {t.moedaOriginal && <span className="ml-2 text-2xs text-muted">({t.moedaOriginal} {formatMoney(t.valor)})</span>}
                      </Td>
                      <Td className="text-muted">{t.categoria ?? '—'}</Td>
                      <Td className={`whitespace-nowrap text-right font-semibold ${t.valor >= 0 ? 'text-income' : 'text-expense'}`}>
                        {formatMoney(t.moedaOriginal && t.valorEmReais != null ? t.valorEmReais : t.valor)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              {proximoCursor && (
                <div className="flex justify-center border-t border-border/70 p-3">
                  <Button variant="soft-info" loading={carregandoMais} onClick={() => setCursores((c) => [...c, proximoCursor])}>Carregar mais</Button>
                </div>
              )}
            </>
          )}
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader title="Contas Pluggy" />
      <Tabs tabs={ABAS} active={aba} onChange={setAba} className="mb-4" />

      {aba === 'historico' && <HistoricoTab />}
      {aba === 'categorias' && <RegrasCategorizacaoTab />}

      {aba === 'contas' && (
      <>
      <Card>
        <CardHeader
          title="Contas sincronizadas"
          actions={
            <>
              {!desligada && <Button data-tutorial="conta.conectar" icon="ri-add-line" onClick={() => { setAtualizarItemId(null); setAviso(true) }} loading={pedirToken.isPending && !atualizarItemId}>Conectar novo banco</Button>}
              {!desligada && itensConectados.length > 0 && (
                <div className="w-52">
                  <Select
                    aria-label="Atualizar conexão" value=""
                    disabled={pedirToken.isPending}
                    onChange={(e) => { if (e.target.value) { setAtualizarItemId(e.target.value); setAviso(true) } }}
                  >
                    <option value="">Atualizar conexão...</option>
                    {itensConectados.map((i) => <option key={i.itemId} value={i.itemId}>{i.rotulo}</option>)}
                  </Select>
                </div>
              )}
              {!desligada && (
                <Button
                  variant="soft-info" icon="ri-refresh-line" loading={isFetching}
                  onClick={() => refetch().then((r) => { if (r.error) { revalidarSePluggyDesligada(qc, r.error); toast.error(mensagemErroPluggy(r.error)) } })}
                >
                  Atualizar
                </Button>
              )}
            </>
          }
        />
        {desligada && <div className="p-4 pb-0"><AvisoPluggyDesligada mensagem={status.mensagem} /></div>}
        {desligada ? (
          <VinculosSemConexao vinculos={vinculos} minhasContas={minhasContas} meusCartoes={meusCartoes} />
        ) : isLoading || !status.pronto ? (
          <LoadingState />
        ) : isError && contas.length === 0 ? (
          // Uma falha de atualização não pode escrever por cima da lista que já estava na tela — só mostra esse erro
          // cheio quando não há nada pra mostrar, e com o motivo que a API devolveu (plano, credenciais, conexão...).
          <ErrorState message={mensagemErroPluggy(error)} />
        ) : contas.length === 0 ? (
          <EmptyState icon="ri-plug-line" message="Nenhuma conta encontrada." />
        ) : (
          <>
            {grupos.map(([chave, lista]) => (
              <div key={chave} className="border-b border-border/70 p-4">
                <p className="mb-1 flex flex-wrap items-center gap-2 text-13 font-medium text-white">
                  <Badge variant="warning">Possível duplicada</Badge>
                  {lista[0].instituicaoNome} · {lista[0].nome}{lista[0].numeroFinal ? ` · final ${lista[0].numeroFinal}` : ''}
                </p>
                <p className="mb-3 text-13 text-muted">Mesma conta em mais de uma conexão. Mantenha uma e exclua as outras.</p>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{lista.map((c) => cartaoConta(c, true))}</div>
              </div>
            ))}
            {semGrupo.length > 0 && <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">{semGrupo.map((c) => cartaoConta(c, false))}</div>}
          </>
        )}
      </Card>

      <CaixaDeEntrada vinculos={vinculos} minhasContas={minhasContas} />
      </>
      )}

      <AvisoConexao open={aviso && !desligada} onCancelar={() => { setAviso(false); setAtualizarItemId(null) }} onContinuar={() => { setAviso(false); itemRegistrado.current = null; setItemNoWidget(atualizarItemId); pedirToken.mutate(atualizarItemId ?? undefined) }} />

      {conectar && !desligada && (
        <PluggyConnect
          connectToken={conectar.accessToken}
          includeSandbox={conectar.incluirSandbox}
          updateItem={itemNoWidget ?? undefined}
          onSuccess={(dados) => { setConectar(null); registrarSeNovo(dados.item.id) }}
          onEvent={(payload) => { if (payload.event === 'ITEM_RESPONSE') registrarSeNovo(payload.item.id) }}
          onError={() => toast.error('Não foi possível conectar o banco.')}
          onClose={() => { setConectar(null); setItemNoWidget(null); setAtualizarItemId(null); itemRegistrado.current = null }}
        />
      )}

      {duplicidade && (
        <DialogoDuplicidade duplicidade={duplicidade.dados} onEscolher={resolverDuplicidade} onClose={() => setDuplicidade(null)} />
      )}

      {excluindo && <ExcluirDuplicada {...excluindo} onClose={() => setExcluindo(null)} />}

      {vinculando && (
        <ModalVincular
          conta={vinculando}
          minhasContas={minhasContas}
          meusCartoes={meusCartoes}
          onClose={() => setVinculando(null)}
          onVinculado={(v) => { if (v.configuracaoStatus === PluggyConfiguracaoStatus.Pendente) setConfigurandoId(v.id) }}
        />
      )}

      {configurandoId && <AssistenteCorte vinculoId={configurandoId} onClose={() => setConfigurandoId(null)} />}

      {refazendo && (
        <RefazerConfiguracao
          vinculoId={refazendo.id}
          nomeAlvo={refazendo.nome}
          onClose={() => setRefazendo(null)}
          onRefeito={() => { setConfigurandoId(refazendo.id); setRefazendo(null) }}
        />
      )}
    </>
  )
}

/** Pluggy desligada: a lista de contas da Pluggy não existe, mas os vínculos continuam (dá para desvincular). */
function VinculosSemConexao({ vinculos, minhasContas, meusCartoes }: { vinculos: PluggyVinculoInfo[]; minhasContas: FinancialAccount[]; meusCartoes: CreditCard[] }) {
  if (vinculos.length === 0) return <EmptyState icon="ri-plug-line" message="Nenhuma conta vinculada." />
  return (
    <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">
      {vinculos.map((v) => {
        const alvo = v.financialAccountId
          ? minhasContas.find((a) => a.id === v.financialAccountId)?.name
          : v.cartaoId ? meusCartoes.find((k) => k.id === v.cartaoId)?.nome : undefined
        return (
          <div key={v.id} className="rounded border border-border/70 bg-surface2/40 p-4">
            <p className="truncate text-2xs uppercase text-muted">{v.banco ?? 'Banco'}</p>
            <p className="mb-3 truncate font-medium text-white">{v.contaPluggyNome ?? 'Conta conectada'}</p>
            <VinculoInfo vinculo={v} nomeAlvo={alvo} onConfigurar={() => undefined} onRefazer={() => undefined} semPluggy />
          </div>
        )
      })}
    </div>
  )
}

function VinculoInfo({ vinculo, nomeAlvo, onConfigurar, onRefazer, semPluggy }: {
  vinculo: PluggyVinculoInfo
  nomeAlvo?: string
  onConfigurar: () => void
  onRefazer: () => void
  /** Pluggy desligada: configurar e refazer dependem dela; desvincular e o aceite automático continuam. */
  semPluggy?: boolean
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const pendente = vinculo.configuracaoStatus === PluggyConfiguracaoStatus.Pendente

  const atualizar = useMutation({
    mutationFn: (v: boolean) => pluggyVinculosApi.atualizar(vinculo.id, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pluggy-vinculos'] }),
    onError: (e: Error) => toast.error(e.message),
  })
  const desvincular = useMutation({
    mutationFn: () => pluggyVinculosApi.desvincular(vinculo.id),
    onSuccess: () => { toast.success('Desvinculado.'); qc.invalidateQueries({ queryKey: ['pluggy-vinculos'] }) },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-13 text-muted">Vinculado a <strong className="text-white">{nomeAlvo ?? '—'}</strong></span>
        <div className="flex">
          {!semPluggy && <IconButton icon="ri-restart-line" label="Refazer configuração" tone="muted" onClick={onRefazer} />}
          <IconButton icon="ri-link-unlink" label="Desvincular" tone="danger" onClick={() => desvincular.mutate()} />
        </div>
      </div>
      {pendente && (
        <div className="flex items-center justify-between gap-2">
          <Badge variant="warning">Configuração pendente</Badge>
          {!semPluggy && <Button size="sm" variant="soft-primary" icon="ri-settings-3-line" onClick={onConfigurar}>Configurar</Button>}
        </div>
      )}
      <Toggle label="Aceitar automaticamente" checked={vinculo.aceitarAutomatico} onChange={(v) => atualizar.mutate(v)} disabled={atualizar.isPending} />
    </div>
  )
}

function ModalVincular({ conta, minhasContas, meusCartoes, onClose, onVinculado }: {
  conta: ContaPluggyInfo
  minhasContas: FinancialAccount[]
  meusCartoes: CreditCard[]
  onClose: () => void
  onVinculado: (v: PluggyVinculoInfo) => void
}) {
  const ehCartao = conta.tipo === 'CREDIT'
  const qc = useQueryClient()
  const toast = useToast()
  const [selecionado, setSelecionado] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  // 409: a conta/cartão do app já tem vínculo com outra conta da Pluggy.
  const [conflito, setConflito] = useState<PluggyVinculoInfo | null>(null)

  // Novo (conta)
  const [nome, setNome] = useState(conta.instituicaoNome)
  const [tipo, setTipo] = useState<AccountType>(AccountType.Checking)
  // Novo (cartão)
  const [contaPagadoraId, setContaPagadoraId] = useState('')
  const [limite, setLimite] = useState('')
  const [diaFechamento, setDiaFechamento] = useState('25')
  const [diaVencimento, setDiaVencimento] = useState('5')

  const vincular = useMutation({
    mutationFn: async (trocar: boolean) => {
      let idAlvo = selecionado
      if (selecionado === CRIAR_NOVA) {
        if (ehCartao) {
          if (!contaPagadoraId) throw new Error('Escolha a conta que paga a fatura.')
          const criado = await cartoesApi.criar({
            nome: nome.trim(), accountId: contaPagadoraId,
            limite: Number(limite) || 0,
            diaFechamento: Number(diaFechamento), diaVencimento: Number(diaVencimento),
          })
          idAlvo = criado.id
        } else {
          const criada = await contasApi.criar({ name: nome.trim(), institution: conta.instituicaoNome, type: tipo, balance: conta.saldo })
          idAlvo = criada.id
        }
      }
      return pluggyVinculosApi.vincular({
        pluggyAccountId: conta.accountId,
        financialAccountId: ehCartao ? undefined : idAlvo,
        cartaoId: ehCartao ? idAlvo : undefined,
        trocar: trocar || undefined,
      })
    },
    onSuccess: (v) => {
      toast.success(conflito ? 'Vínculo trocado.' : 'Conta vinculada.')
      if (v?.itemAntigoExcluidoNaPluggy === false) toast.error(v.aviso || 'A conexão antiga não foi excluída na Pluggy. Exclua-a depois.')
      qc.invalidateQueries({ queryKey: ['pluggy-vinculos'] })
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['cartoes'] })
      onClose()
      onVinculado(v)
    },
    onError: (e: Error & { response?: { status?: number; data?: { data?: PluggyVinculoInfo | null } } }) => {
      if (e.response?.status === 409) {
        setConflito(e.response.data?.data ?? ({} as PluggyVinculoInfo))
        setErro(null)
        return
      }
      setErro(e.message)
    },
  })

  function submit() {
    if (!selecionado) { setErro('Escolha uma opção.'); return }
    if (selecionado === CRIAR_NOVA && !nome.trim()) { setErro('Informe o nome.'); return }
    vincular.mutate(false)
  }

  const opcoesExistentes = ehCartao ? meusCartoes : minhasContas

  return (
    <Modal
      open
      onClose={onClose}
      title={`Vincular · ${conta.instituicaoNome} · ${conta.nome}`}
      footer={
        <>
          <Button variant="light" onClick={onClose}>Cancelar</Button>
          {conflito ? (
            <Button icon="ri-swap-line" onClick={() => vincular.mutate(true)} loading={vincular.isPending}>Trocar</Button>
          ) : (
            <Button icon="ri-link" onClick={submit} loading={vincular.isPending}>
              {selecionado === CRIAR_NOVA ? 'Criar e vincular' : 'Vincular'}
            </Button>
          )}
        </>
      }
    >
      {conflito ? (
        <div className="space-y-3 text-13 text-muted" role="alert">
          <p className="text-white">
            {ehCartao ? 'Este cartão' : 'Esta conta'} já está vinculada {conflito.contaPluggyNome ? <>à conta <strong>{conflito.contaPluggyNome}</strong> da Pluggy</> : 'a outra conta da Pluggy'}
            {conflito.banco ? <> (banco {conflito.banco})</> : null}. Quer trocar o vínculo?
          </p>
          <p>A troca não altera lançamentos nem saldo. O vínculo novo começa pendente de configuração.</p>
        </div>
      ) : (
      <div className="space-y-4">
        <Select label={ehCartao ? 'Cartão do Walleon' : 'Conta do Walleon'} value={selecionado} onChange={(e) => setSelecionado(e.target.value)}>
          <option value="">Selecione...</option>
          {opcoesExistentes.map((o) => (
            <option key={o.id} value={o.id}>{'name' in o ? o.name : o.nome}</option>
          ))}
          <option value={CRIAR_NOVA}>+ Criar {ehCartao ? 'novo cartão' : 'nova conta'}</option>
        </Select>

        {selecionado === CRIAR_NOVA && !ehCartao && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Nome da conta" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
            <Select label="Tipo" value={tipo} onChange={(e) => setTipo(Number(e.target.value) as AccountType)}>
              {Object.entries(ACCOUNT_TYPE_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
            </Select>
            <p className="text-13 text-muted sm:col-span-2">Saldo inicial: <strong className="text-white">{formatMoney(conta.saldo)}</strong> (o saldo da Pluggy)</p>
          </div>
        )}

        {selecionado === CRIAR_NOVA && ehCartao && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Nome do cartão" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
            <Select label="Conta que paga a fatura" value={contaPagadoraId} onChange={(e) => setContaPagadoraId(e.target.value)}>
              <option value="">Selecione...</option>
              {minhasContas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
            <Input label="Limite (R$)" type="number" step="0.01" value={limite} onChange={(e) => setLimite(e.target.value)} />
            <div className="flex gap-4">
              <Input label="Dia de fechamento" type="number" min={1} max={28} value={diaFechamento} onChange={(e) => setDiaFechamento(e.target.value)} />
              <Input label="Dia de vencimento" type="number" min={1} max={28} value={diaVencimento} onChange={(e) => setDiaVencimento(e.target.value)} />
            </div>
          </div>
        )}

        {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
      </div>
      )}
    </Modal>
  )
}

function CaixaDeEntrada({ vinculos, minhasContas }: { vinculos: PluggyVinculoInfo[]; minhasContas: FinancialAccount[] }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [pagina, setPagina] = useState(1)
  const [contaEscolhida, setContaEscolhida] = useState<Record<string, string>>({})
  const [compraEscolhida, setCompraEscolhida] = useState<Record<string, string>>({})
  const [edicao, setEdicao] = useState<Record<string, { descricao?: string; valor?: number }>>({})
  const [editando, setEditando] = useState<PluggyPendenciaInfo | null>(null)
  const porPagina = 10

  const { data: pendencias = [], isLoading } = useQuery({ queryKey: ['pluggy-pendencias'], queryFn: pluggyPendenciasApi.list })

  function invalidarTudo() {
    qc.invalidateQueries({ queryKey: ['pluggy-pendencias'] })
    qc.invalidateQueries({ queryKey: ['pluggy-historico'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['contas'] })
    qc.invalidateQueries({ queryKey: ['cartoes'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const aceitar = useMutation({
    mutationFn: ({ id }: { id: string }) => pluggyPendenciasApi.aceitar(id, undefined, edicao[id]?.descricao, edicao[id]?.valor),
    onSuccess: () => { toast.success('Lançamento criado.'); invalidarTudo() },
    onError: (e: Error) => toast.error(e.message),
  })
  const vincularSugerido = useMutation({
    mutationFn: (id: string) => pluggyPendenciasApi.vincular(id),
    onSuccess: () => { toast.success('Vinculado ao lançamento existente.'); invalidarTudo() },
    onError: (e: Error) => toast.error(e.message),
  })
  const manterSeparada = useMutation({
    mutationFn: ({ id }: { id: string }) => pluggyPendenciasApi.manterSeparada(id, undefined, edicao[id]?.descricao, edicao[id]?.valor),
    onSuccess: () => { toast.success('Lançamento criado separadamente.'); invalidarTudo() },
    onError: (e: Error) => toast.error(e.message),
  })
  const confirmarTransferencia = useMutation({
    mutationFn: ({ id, contaId }: { id: string; contaId: string }) => pluggyPendenciasApi.confirmarTransferencia(id, contaId),
    onSuccess: () => { toast.success('Transferência lançada.'); invalidarTudo() },
    onError: (e: Error) => toast.error(e.message),
  })
  const acao = (fn: (v: any) => Promise<unknown>, msg: string) => ({ mutationFn: fn, onSuccess: () => { toast.success(msg); invalidarTudo() }, onError: (e: Error) => toast.error(e.message) })
  const confirmarPar = useMutation(acao((id: string) => pluggyPendenciasApi.confirmarPar(id), 'Transferência lançada.'))
  const vincularTransferencia = useMutation(acao((id: string) => pluggyPendenciasApi.vincularTransferencia(id), 'Vinculado à transferência que você já lançou.'))
  const vincularFatura = useMutation(acao((v: { id: string; faturaId: string }) => pluggyPendenciasApi.vincularFatura(v.id, v.faturaId), 'Pagamento vinculado à fatura.'))
  const pagarFatura = useMutation(acao((v: { id: string; faturaId: string }) => pluggyPendenciasApi.pagarFatura(v.id, v.faturaId), 'Fatura paga.'))
  const ignorar = useMutation(acao((id: string) => pluggyPendenciasApi.ignorar(id), 'Ignorado.'))
  const confirmarEstorno = useMutation({
    mutationFn: ({ id, compraId }: { id: string; compraId: string }) => pluggyPendenciasApi.confirmarEstorno(id, compraId),
    onSuccess: () => { toast.success('Estorno lançado na fatura.'); invalidarTudo() },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!isLoading && pendencias.length === 0) return null

  const totalPaginas = Math.max(1, Math.ceil(pendencias.length / porPagina))
  const paginaAtual = pendencias.slice((pagina - 1) * porPagina, pagina * porPagina)

  return (
    <Card className="mt-4">
      <span id="caixa-de-entrada" className="block scroll-mt-20" />
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            Caixa de entrada
            {pendencias.length > 0 && <Badge variant="warning">{pendencias.length}</Badge>}
          </span>
        }
        actions={<AceitarTudoButton pendencias={pendencias} variant="soft-primary" />}
      />
      {isLoading ? <LoadingState /> : (
        <Table>
          <THead>
            <tr><Th>Data</Th><Th>Descrição</Th><Th className="text-right">Valor</Th><Th>Situação</Th><Th className="text-right">Ações</Th></tr>
          </THead>
          <tbody>
            {paginaAtual.map((p) => (
              <PendenciaLinha
                key={p.id} p={p} vinculo={vinculos.find((v) => v.id === p.vinculoId)} minhasContas={minhasContas}
                edicao={edicao[p.id]} onEditar={() => setEditando(p)}
                contaEscolhida={contaEscolhida[p.id] ?? ''} onContaEscolhida={(v) => setContaEscolhida((s) => ({ ...s, [p.id]: v }))}
                compraEscolhida={compraEscolhida[p.id] ?? ''} onCompraEscolhida={(v) => setCompraEscolhida((s) => ({ ...s, [p.id]: v }))}
                aceitar={aceitar} vincularSugerido={vincularSugerido} manterSeparada={manterSeparada}
                confirmarTransferencia={confirmarTransferencia} confirmarEstorno={confirmarEstorno}
                confirmarPar={confirmarPar} vincularTransferencia={vincularTransferencia} vincularFatura={vincularFatura} pagarFatura={pagarFatura} ignorar={ignorar}
              />
            ))}
          </tbody>
        </Table>
      )}
      {totalPaginas > 1 && <Pagination page={pagina} totalPages={totalPaginas} onChange={setPagina} summary={`${pendencias.length} pendências`} />}

      <Modal open={!!editando} onClose={() => setEditando(null)} title="Editar antes de aceitar">
        {editando && (
          <div className="space-y-3">
            <Input
              label="Descrição"
              defaultValue={edicao[editando.id]?.descricao ?? editando.descricao}
              onChange={(e) => setEdicao((s) => ({ ...s, [editando.id]: { ...s[editando.id], descricao: e.target.value } }))}
            />
            <Input
              label="Valor" type="number" step="0.01"
              defaultValue={edicao[editando.id]?.valor ?? Math.abs(editando.valor)}
              onChange={(e) => setEdicao((s) => ({ ...s, [editando.id]: { ...s[editando.id], valor: Number(e.target.value) } }))}
              disabled={!!editando.totalParcelas}
            />
            {editando.totalParcelas && <p className="text-2xs text-muted">O valor dessa pendência é o total da compra parcelada e não pode ser editado aqui.</p>}
            <Button className="w-full" onClick={() => setEditando(null)}>Pronto</Button>
          </div>
        )}
      </Modal>
    </Card>
  )
}

function PendenciaLinha({
  p, vinculo, minhasContas, edicao, onEditar,
  contaEscolhida, onContaEscolhida, compraEscolhida, onCompraEscolhida,
  aceitar, vincularSugerido, manterSeparada, confirmarTransferencia, confirmarEstorno,
  confirmarPar, vincularTransferencia, vincularFatura, pagarFatura, ignorar,
}: {
  p: PluggyPendenciaInfo
  vinculo?: PluggyVinculoInfo
  minhasContas: FinancialAccount[]
  edicao?: { descricao?: string; valor?: number }
  onEditar: () => void
  contaEscolhida: string
  onContaEscolhida: (v: string) => void
  compraEscolhida: string
  onCompraEscolhida: (v: string) => void
  aceitar: ReturnType<typeof useMutation<unknown, Error, { id: string }>>
  vincularSugerido: ReturnType<typeof useMutation<unknown, Error, string>>
  manterSeparada: ReturnType<typeof useMutation<unknown, Error, { id: string }>>
  confirmarTransferencia: ReturnType<typeof useMutation<unknown, Error, { id: string; contaId: string }>>
  confirmarEstorno: ReturnType<typeof useMutation<unknown, Error, { id: string; compraId: string }>>
  confirmarPar: ReturnType<typeof useMutation<unknown, Error, string>>
  vincularTransferencia: ReturnType<typeof useMutation<unknown, Error, string>>
  vincularFatura: ReturnType<typeof useMutation<unknown, Error, { id: string; faturaId: string }>>
  pagarFatura: ReturnType<typeof useMutation<unknown, Error, { id: string; faturaId: string }>>
  ignorar: ReturnType<typeof useMutation<unknown, Error, string>>
}) {
  const contasParaEscolher = minhasContas.filter((c) => c.id !== vinculo?.financialAccountId)
  const ehEstorno = p.tipo === PluggyPendenciaTipo.Estorno
  const compras = useQuery({
    queryKey: ['pluggy-compras-estorno', p.id],
    queryFn: () => pluggyPendenciasApi.comprasParaEstorno(p.id),
    enabled: ehEstorno,
  })
  const podeEditar = p.tipo === PluggyPendenciaTipo.Nova || p.tipo === PluggyPendenciaTipo.PossivelDuplicata

  return (
    <Tr>
      <Td className="whitespace-nowrap text-muted">{formatDate(p.data)}</Td>
      <Td className="font-medium text-white">
        {edicao?.descricao ?? p.descricao}
        {p.contaNome && <span className="block text-2xs font-normal text-muted">{p.contaNome}{p.tipoPluggy ? (p.tipoPluggy === 'DEBIT' ? ' · saiu desta conta' : ' · entrou nesta conta') : ''}</span>}
        {p.categoriaSugerida && <span className="ml-2 text-2xs text-muted">({p.categoriaSugerida})</span>}
        {p.moedaNaoConvertida && (
          <span className="ml-2 inline-flex items-center gap-1 text-2xs text-warning" title="Não deu pra converter pra BRL: confira o valor antes de aceitar.">
            <i className="ri-error-warning-line" aria-hidden /> {p.moedaOriginal}
          </span>
        )}
      </Td>
      <Td className={`whitespace-nowrap text-right font-semibold ${p.valor >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(edicao?.valor ?? p.valor)}</Td>
      <Td>
        {p.tipo === PluggyPendenciaTipo.Transferencia ? (
          <div className="space-y-1">
            <Badge variant="info">Transferência</Badge>
            {p.parPendenciaId && (
              <p className="text-13 text-white">
                {p.tipoPluggy === 'DEBIT' ? `De ${p.contaNome} para ${p.parContaNome}` : `De ${p.parContaNome} para ${p.contaNome}`}
                <span className="block text-muted">A outra ponta aparece na lista também; confirmar uma resolve as duas.</span>
              </p>
            )}
            {!p.parPendenciaId && p.sugeridoEhTransferencia && (
              <p className="text-13 text-muted">Você já lançou esta transferência: <strong className="text-white">{p.financialTransactionDescricaoSugerida}</strong></p>
            )}
            {p.financialTransactionIdSugerido && !p.sugeridoEhTransferencia && (
              <p className="text-13 text-muted">
                Pode já estar lançado como <strong className="text-white">{p.financialTransactionDescricaoSugerida}</strong>
                {p.financialTransactionDataSugerida && ` de ${formatDate(p.financialTransactionDataSugerida)}`} — confira antes de confirmar.
              </p>
            )}
          </div>
        ) : p.tipo === PluggyPendenciaTipo.PagamentoFatura ? (
          <div className="space-y-1">
            <Badge variant="info">Pagamento da fatura do cartão</Badge>
            {p.faturaPagaId ? <p className="text-13 text-white">Parece o pagamento da fatura {p.faturaPagaRotulo}.</p>
              : p.faturaAbertaId ? <p className="text-13 text-muted">Nenhuma fatura paga combina. Fatura em aberto mais provável: {p.faturaAbertaRotulo}.</p>
              : <p className="text-13 text-muted">Nenhuma fatura combina com este pagamento.</p>}
          </div>
        ) : p.tipo === PluggyPendenciaTipo.PagamentoFaturaConta ? (
          <PagamentoContaSituacao p={p} />
        ) : ehEstorno ? (
          <Badge variant="info">Estorno (crédito no cartão)</Badge>
        ) : p.tipo === PluggyPendenciaTipo.Nova && p.totalParcelas ? (
          <div className="space-y-1">
            <Badge variant="info">Compra em {p.totalParcelas}x</Badge>
            <p className="text-13 text-muted">Total da compra: <strong className="text-white">{formatMoney(p.valorTotalCompra ?? p.valor)}</strong></p>
          </div>
        ) : p.tipo === PluggyPendenciaTipo.Nova ? (
          <Badge variant="info">Nova</Badge>
        ) : (
          <span className="text-13 text-muted">
            Parece com <strong className="text-white">{p.financialTransactionDescricaoSugerida}</strong>
            {p.financialTransactionDataSugerida && ` de ${formatDate(p.financialTransactionDataSugerida)}`}
          </span>
        )}
      </Td>
      <Td className="text-right">
        <div className="flex justify-end items-center gap-2">
          {podeEditar && <IconButton icon="ri-pencil-fill" label="Editar antes de aceitar" tone="muted" onClick={onEditar} />}
          {p.tipo === PluggyPendenciaTipo.PagamentoFatura ? (
            <>
              {p.faturaPagaId ? (
                <Button variant="soft-primary" loading={vincularFatura.isPending} onClick={() => vincularFatura.mutate({ id: p.id, faturaId: p.faturaPagaId! })}>Vincular à fatura paga</Button>
              ) : p.faturaAbertaId ? (
                <Button variant="soft-primary" loading={pagarFatura.isPending}
                  onClick={() => { if (window.confirm(`Marcar a fatura ${p.faturaAbertaRotulo} como paga em ${formatDate(p.data)} e debitar a conta do cartão?`)) pagarFatura.mutate({ id: p.id, faturaId: p.faturaAbertaId! }) }}>Pagar essa fatura</Button>
              ) : null}
              <Button variant="light" loading={ignorar.isPending} onClick={() => ignorar.mutate(p.id)}>Ignorar</Button>
            </>
          ) : p.tipo === PluggyPendenciaTipo.PagamentoFaturaConta ? (
            <Button variant="light" loading={ignorar.isPending} onClick={() => ignorar.mutate(p.id)}>Ignorar</Button>
          ) : p.tipo === PluggyPendenciaTipo.Transferencia && (p.parPendenciaId || p.sugeridoEhTransferencia) ? (
            <>
              {p.parPendenciaId ? (
                <Button variant="soft-primary" loading={confirmarPar.isPending} onClick={() => confirmarPar.mutate(p.id)}>Confirmar transferência</Button>
              ) : (
                <Button variant="soft-primary" loading={vincularTransferencia.isPending} onClick={() => vincularTransferencia.mutate(p.id)}>É a que eu já lancei</Button>
              )}
              <div className="w-44">
                <Select aria-label="Escolher outra conta" value={contaEscolhida} onChange={(e) => onContaEscolhida(e.target.value)}>
                  <option value="">Outra conta...</option>
                  {contasParaEscolher.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <Button variant="light" loading={confirmarTransferencia.isPending} disabled={!contaEscolhida}
                onClick={() => confirmarTransferencia.mutate({ id: p.id, contaId: contaEscolhida })}>Confirmar</Button>
            </>
          ) : p.tipo === PluggyPendenciaTipo.Transferencia ? (
            <>
              <div className="w-44">
                <Select aria-label="Conta do outro lado" value={contaEscolhida} onChange={(e) => onContaEscolhida(e.target.value)}>
                  <option value="">Escolha a conta...</option>
                  {contasParaEscolher.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <Button
                variant="soft-primary" loading={confirmarTransferencia.isPending}
                disabled={!contaEscolhida}
                onClick={() => confirmarTransferencia.mutate({ id: p.id, contaId: contaEscolhida })}
              >Confirmar</Button>
            </>
          ) : ehEstorno ? (
            <>
              <div className="w-56">
                <Select
                  aria-label="Compra estornada" value={compraEscolhida} onChange={(e) => onCompraEscolhida(e.target.value)}
                  disabled={compras.isLoading || (compras.data?.length ?? 0) === 0}
                >
                  <option value="">{compras.isLoading ? 'Carregando compras...' : (compras.data?.length ?? 0) === 0 ? 'Nenhuma compra nessa fatura' : 'Escolha a compra...'}</option>
                  {(compras.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.description} · {formatMoney(c.amount)} · {formatDate(c.date)}</option>)}
                </Select>
              </div>
              <Button
                variant="soft-primary" loading={confirmarEstorno.isPending}
                disabled={!compraEscolhida}
                onClick={() => confirmarEstorno.mutate({ id: p.id, compraId: compraEscolhida })}
              >Confirmar</Button>
            </>
          ) : p.tipo === PluggyPendenciaTipo.Nova ? (
            <Button variant="soft-primary" loading={aceitar.isPending} onClick={() => aceitar.mutate({ id: p.id })}>
              {p.totalParcelas ? `Lançar em ${p.totalParcelas}x` : 'Aceitar'}
            </Button>
          ) : (
            <>
              <Button variant="soft-info" loading={vincularSugerido.isPending} onClick={() => vincularSugerido.mutate(p.id)}>É o mesmo</Button>
              <Button variant="light" loading={manterSeparada.isPending} onClick={() => manterSeparada.mutate({ id: p.id })}>Manter separado</Button>
            </>
          )}
        </div>
      </Td>
    </Tr>
  )
}

function HistoricoTab() {
  const { data: itens = [], isLoading } = useQuery({ queryKey: ['pluggy-historico'], queryFn: pluggyHistoricoApi.list })
  const ordenacao = useOrdenarBuscarLocal(itens, { nome: (it) => it.descricao, data: (it) => it.data, valor: (it) => it.valor })

  return (
    <Card>
      <CardHeader title="Histórico de transações trazidas pela Pluggy" />
      {isLoading ? <LoadingState /> : itens.length === 0 ? (
        <EmptyState icon="ri-history-line" message="Nenhuma transação da Pluggy ainda." />
      ) : (
        <>
        <div className="border-b border-border/60 p-4">
          <div className="w-64">
            <Input placeholder="Buscar por nome" value={ordenacao.busca} onChange={(e) => ordenacao.setBusca(e.target.value)} />
          </div>
        </div>
        <Table>
          <THead>
            <tr>
              <ThOrdenavel campo="data" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao}>Data</ThOrdenavel>
              <ThOrdenavel campo="nome" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao}>Descrição</ThOrdenavel>
              <ThOrdenavel campo="valor" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao} className="text-right">Valor</ThOrdenavel>
              <Th>Status</Th>
              <Th className="text-right">Ações</Th>
            </tr>
          </THead>
          <tbody>
            {ordenacao.itens.map((it) => (
              <Tr key={it.id}>
                <Td className="whitespace-nowrap text-muted">{formatDate(it.data)}</Td>
                <Td className="font-medium text-white">
                  {it.descricao}
                  {it.moedaNaoConvertida && <span className="ml-2 text-2xs text-warning" title="Moeda não convertida"><i className="ri-error-warning-line" aria-hidden /></span>}
                </Td>
                <Td className={`whitespace-nowrap text-right font-semibold ${it.valor >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(it.valor)}</Td>
                <Td><Badge variant={it.statusDescricao === 'Pendente' ? 'warning' : 'income'}>{it.statusDescricao}</Badge></Td>
                <Td className="text-right">
                  {it.financialTransactionId && (
                    <Link to={`/transacoes?editar=${it.financialTransactionId}`}>
                      <IconButton icon="ri-pencil-fill" label={`Editar ${it.descricao}`} tone="info" />
                    </Link>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        </>
      )}
    </Card>
  )
}

const TIPO_PLUGGY_LABEL: Record<string, string> = { DEBIT: 'Débito (saída)', CREDIT: 'Crédito (entrada)' }

function novaCondicao(): CondicaoRegra {
  return { campo: CampoRegraCategorizacao.Descricao, operador: OperadorRegraCategorizacao.Contem, valores: [''] }
}

function descreverCondicao(c: CondicaoRegra): string {
  const campo = CAMPO_REGRA_LABEL[c.campo]
  const op = OPERADOR_REGRA_LABEL[c.operador]
  if (c.operador === OperadorRegraCategorizacao.Entre) return `${campo} ${op} ${c.valores[0] || '?'} e ${c.valores[1] || '?'}`
  const valoresLabel = c.campo === CampoRegraCategorizacao.Tipo
    ? c.valores.map((v) => TIPO_PLUGGY_LABEL[v] ?? v)
    : c.valores
  return `${campo} ${op} ${valoresLabel.filter(Boolean).map((v) => `"${v}"`).join(' ou ')}`
}

function RegrasCategorizacaoTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const [modalAberto, setModalAberto] = useState(false)
  const [editando, setEditando] = useState<RegraCategorizacao | null>(null)

  const { data: regras = [], isLoading } = useQuery({ queryKey: ['regras-categorizacao'], queryFn: regrasCategorizacaoApi.list })
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }) })

  const remover = useMutation({
    mutationFn: (id: string) => regrasCategorizacaoApi.remover(id),
    onSuccess: () => { toast.success('Regra removida.'); qc.invalidateQueries({ queryKey: ['regras-categorizacao'] }) },
    onError: (e: Error) => toast.error(e.message),
  })
  const reordenar = useMutation({
    mutationFn: (idsNaOrdem: string[]) => regrasCategorizacaoApi.reordenar(idsNaOrdem),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regras-categorizacao'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  function mover(index: number, dir: -1 | 1) {
    const alvo = index + dir
    if (alvo < 0 || alvo >= regras.length) return
    const ids = regras.map((r) => r.id)
    ;[ids[index], ids[alvo]] = [ids[alvo], ids[index]]
    reordenar.mutate(ids)
  }

  function abrirNova() { setEditando(null); setModalAberto(true) }
  function abrirEdicao(r: RegraCategorizacao) { setEditando(r); setModalAberto(true) }

  return (
    <Card>
      <CardHeader
        title="Regras de categorização automática"
        actions={<Button icon="ri-add-line" onClick={abrirNova}>Nova regra</Button>}
      />
      <p className="border-b border-border/60 px-4 py-3 text-13 text-muted">
        Quando uma transação chega da Pluggy, testamos as regras nesta ordem — a primeira que bater define a categoria.
        Sem nenhuma regra correspondente, tentamos pelo nome exato da categoria sugerida.
      </p>
      {isLoading ? <LoadingState /> : regras.length === 0 ? (
        <EmptyState icon="ri-git-branch-line" message="Nenhuma regra cadastrada ainda." />
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Ordem</Th><Th>Nome</Th><Th>Condições</Th><Th>Categoria</Th><Th>Status</Th><Th className="text-right">Ações</Th>
            </tr>
          </THead>
          <tbody>
            {regras.map((r, i) => (
              <Tr key={r.id}>
                <Td className="whitespace-nowrap">
                  <div className="flex items-center gap-0.5">
                    <IconButton icon="ri-arrow-up-line" label="Mover para cima" disabled={i === 0 || reordenar.isPending} onClick={() => mover(i, -1)} />
                    <IconButton icon="ri-arrow-down-line" label="Mover para baixo" disabled={i === regras.length - 1 || reordenar.isPending} onClick={() => mover(i, 1)} />
                  </div>
                </Td>
                <Td className="font-medium text-white">{r.nome}</Td>
                <Td className="max-w-sm text-13 text-muted">
                  {r.condicoes.map((c, ci) => <div key={ci}>{ci > 0 && <span className="text-white">E </span>}{descreverCondicao(c)}</div>)}
                </Td>
                <Td className="text-muted">{r.categoriaNome}</Td>
                <Td><Badge variant={r.ativa ? 'income' : 'muted'}>{r.ativa ? 'Ativa' : 'Inativa'}</Badge></Td>
                <Td className="text-right">
                  <IconButton icon="ri-pencil-fill" label={`Editar ${r.nome}`} tone="info" onClick={() => abrirEdicao(r)} />
                  <IconButton icon="ri-delete-bin-5-fill" label={`Remover ${r.nome}`} tone="danger" onClick={() => remover.mutate(r.id)} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {modalAberto && <RegraFormModal regra={editando} categorias={categorias} onClose={() => setModalAberto(false)} />}
    </Card>
  )
}

function RegraFormModal({ regra, categorias, onClose }: { regra: RegraCategorizacao | null; categorias: Categoria[]; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [nome, setNome] = useState(regra?.nome ?? '')
  const [categoriaId, setCategoriaId] = useState(regra?.categoriaId ?? '')
  const [ativa, setAtiva] = useState(regra?.ativa ?? true)
  const [condicoes, setCondicoes] = useState<CondicaoRegra[]>(regra?.condicoes.length ? regra.condicoes.map((c) => ({ ...c, valores: [...c.valores] })) : [novaCondicao()])
  const [resultadoTeste, setResultadoTeste] = useState<number | null>(null)

  const { data: categoriasPluggy = [] } = useQuery({ queryKey: ['categorias-pluggy-conhecidas'], queryFn: regrasCategorizacaoApi.categoriasConhecidas })

  const payload = { nome: nome.trim(), categoriaId, ativa, condicoes: condicoes.map((c) => ({ ...c, valores: c.valores.map((v) => v.trim()).filter(Boolean) })) }
  const valido = !!payload.nome && !!payload.categoriaId && condicoes.every((c) => c.valores.some((v) => v.trim()))

  const salvar = useMutation({
    mutationFn: () => regra ? regrasCategorizacaoApi.atualizar(regra.id, payload) : regrasCategorizacaoApi.criar(payload),
    onSuccess: () => {
      toast.success('Regra salva.')
      qc.invalidateQueries({ queryKey: ['regras-categorizacao'] })
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const testar = useMutation({
    mutationFn: () => regrasCategorizacaoApi.testar(payload),
    onSuccess: (n) => setResultadoTeste(n),
    onError: (e: Error) => toast.error(e.message),
  })

  function atualizarCondicao(i: number, patch: Partial<CondicaoRegra>) {
    setResultadoTeste(null)
    setCondicoes((atual) => atual.map((c, ci) => (ci === i ? { ...c, ...patch } : c)))
  }
  function mudarCampo(i: number, campo: CampoRegraCategorizacao) {
    const operador = OPERADORES_POR_CAMPO[campo][0]
    atualizarCondicao(i, { campo, operador, valores: [''] })
  }
  function mudarOperador(i: number, operador: OperadorRegraCategorizacao) {
    atualizarCondicao(i, { operador, valores: operador === OperadorRegraCategorizacao.Entre ? ['', ''] : [''] })
  }
  function adicionarValor(i: number) {
    atualizarCondicao(i, { valores: [...condicoes[i].valores, ''] })
  }
  function mudarValor(i: number, vi: number, valor: string) {
    const valores = [...condicoes[i].valores]
    valores[vi] = valor
    atualizarCondicao(i, { valores })
  }
  function removerValor(i: number, vi: number) {
    const valores = condicoes[i].valores.filter((_, x) => x !== vi)
    atualizarCondicao(i, { valores: valores.length ? valores : [''] })
  }
  function removerCondicao(i: number) {
    setResultadoTeste(null)
    setCondicoes((atual) => atual.length > 1 ? atual.filter((_, ci) => ci !== i) : atual)
  }

  return (
    <Modal open onClose={onClose} title={regra ? 'Editar regra' : 'Nova regra'} size="lg" footer={
      <>
        <Button variant="soft-info" icon="ri-play-line" loading={testar.isPending} disabled={!valido} onClick={() => testar.mutate()}>Testar</Button>
        <Button variant="light" onClick={onClose}>Cancelar</Button>
        <Button loading={salvar.isPending} disabled={!valido} onClick={() => salvar.mutate()}>Salvar</Button>
      </>
    }>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Nome da regra" placeholder="ex.: Uber e 99 = Transporte" value={nome} onChange={(e) => setNome(e.target.value)} />
          <Select label="Categoria a aplicar" value={categoriaId} onChange={(e) => { setCategoriaId(e.target.value); setResultadoTeste(null) }}>
            <option value="">Selecione...</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
        </div>
        <Toggle label="Regra ativa" checked={ativa} onChange={(v) => { setAtiva(v); setResultadoTeste(null) }} />

        <div>
          <p className="form-label mb-2">Condições (todas precisam bater — E)</p>
          <div className="space-y-3">
            {condicoes.map((c, i) => (
              <div key={i} className="rounded border border-border/70 bg-surface2/40 p-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-40">
                    <Select label="Campo" value={c.campo} onChange={(e) => mudarCampo(i, Number(e.target.value) as CampoRegraCategorizacao)}>
                      {Object.entries(CAMPO_REGRA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Select>
                  </div>
                  <div className="w-40">
                    <Select label="Operador" value={c.operador} onChange={(e) => mudarOperador(i, Number(e.target.value) as OperadorRegraCategorizacao)}>
                      {OPERADORES_POR_CAMPO[c.campo].map((op) => <option key={op} value={op}>{OPERADOR_REGRA_LABEL[op]}</option>)}
                    </Select>
                  </div>
                  <div className="flex-1" />
                  <IconButton icon="ri-delete-bin-5-fill" label="Remover condição" tone="danger" disabled={condicoes.length === 1} onClick={() => removerCondicao(i)} />
                </div>

                {c.operador === OperadorRegraCategorizacao.Entre ? (
                  <div className="mt-2 flex items-center gap-2">
                    <Input type="number" step="0.01" placeholder="Mínimo" value={c.valores[0] ?? ''} onChange={(e) => mudarValor(i, 0, e.target.value)} />
                    <span className="text-muted">e</span>
                    <Input type="number" step="0.01" placeholder="Máximo" value={c.valores[1] ?? ''} onChange={(e) => mudarValor(i, 1, e.target.value)} />
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {c.valores.map((v, vi) => (
                      <div key={vi} className="flex items-center gap-2">
                        {c.campo === CampoRegraCategorizacao.Tipo ? (
                          <Select value={v} onChange={(e) => mudarValor(i, vi, e.target.value)}>
                            <option value="">Selecione...</option>
                            <option value="DEBIT">Débito (saída)</option>
                            <option value="CREDIT">Crédito (entrada)</option>
                          </Select>
                        ) : c.campo === CampoRegraCategorizacao.CategoriaPluggy ? (
                          <>
                            <Input
                              list="categorias-pluggy-conhecidas" placeholder="ex.: Groceries"
                              value={v} onChange={(e) => mudarValor(i, vi, e.target.value)}
                            />
                            <datalist id="categorias-pluggy-conhecidas">
                              {categoriasPluggy.map((cp) => <option key={cp} value={cp} />)}
                            </datalist>
                          </>
                        ) : c.campo === CampoRegraCategorizacao.Valor ? (
                          <Input type="number" step="0.01" placeholder="0,00" value={v} onChange={(e) => mudarValor(i, vi, e.target.value)} />
                        ) : (
                          <Input placeholder={c.campo === CampoRegraCategorizacao.Conta ? 'ex.: Nubank' : 'ex.: uber'} value={v} onChange={(e) => mudarValor(i, vi, e.target.value)} />
                        )}
                        {c.valores.length > 1 && (
                          <IconButton icon="ri-close-line" label="Remover valor" onClick={() => removerValor(i, vi)} />
                        )}
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" icon="ri-add-line" onClick={() => adicionarValor(i)}>Adicionar valor (ou)</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button variant="soft-primary" size="sm" icon="ri-add-line" className="mt-3" onClick={() => { setResultadoTeste(null); setCondicoes((c) => [...c, novaCondicao()]) }}>
            Adicionar condição
          </Button>
        </div>

        {resultadoTeste !== null && (
          <div className="rounded border border-info/30 bg-info/10 p-3 text-13 text-info">
            <i className="ri-information-line" aria-hidden /> Essa regra teria batido em <strong>{resultadoTeste}</strong> transação(ões) da Pluggy nos últimos 90 dias.
          </div>
        )}
      </div>
    </Modal>
  )
}
